/**
 * B2B/Wholesale Pricing System
 * 
 * Handles:
 * - Dynamic price updates based on customer status
 * - Volume/tiered pricing calculations
 * - Quantity-based discount display
 * - Real-time price updates on variant/quantity changes
 */

class B2BPricing {
  constructor() {
    this.init();
  }

  init() {
    // Initialize on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Get all B2B pricing data elements
    this.pricingElements = document.querySelectorAll('[data-b2b-pricing]');
    
    if (this.pricingElements.length === 0) return;

    // Parse pricing data
    this.pricingData = {};
    this.pricingElements.forEach(el => {
      try {
        const data = JSON.parse(el.textContent);
        this.pricingData[data.productId] = data;
      } catch (e) {
        console.error('Failed to parse B2B pricing data:', e);
      }
    });

    // Setup event listeners
    this.setupQuantityListeners();
    this.setupVariantListeners();
    this.updateAllPrices();
    this.renderVolumeTables();
  }

  /**
   * Calculate dealer price with volume discount
   */
  calculateDealerPrice(productId, variantId, quantity = 1) {
    const data = this.pricingData[productId];
    if (!data || !data.isDealer) return null;

    const variantData = data.variantPrices[variantId];
    if (!variantData) return null;

    const retailPrice = variantData.price;
    const baseDiscount = data.dealerDiscount || 20;
    
    // Calculate base dealer price
    let dealerPrice = retailPrice * (100 - baseDiscount) / 100;
    
    // Apply volume discount if applicable
    const volumeDiscount = this.getVolumeDiscount(data.volumeTiers, quantity);
    if (volumeDiscount > 0) {
      dealerPrice = dealerPrice * (100 - volumeDiscount) / 100;
    }

    return {
      retailPrice: retailPrice,
      dealerPrice: dealerPrice,
      baseDiscount: baseDiscount,
      volumeDiscount: volumeDiscount,
      totalDiscount: baseDiscount + volumeDiscount,
      savings: retailPrice - dealerPrice,
      quantity: quantity
    };
  }

  /**
   * Parse volume tiers and get applicable discount
   * Format: "10:10,25:15,50:20" or JSON array
   */
  getVolumeDiscount(volumeTiers, quantity) {
    if (!volumeTiers) return 0;

    let tiers = [];
    
    // Parse string format "10:10,25:15,50:20"
    if (typeof volumeTiers === 'string') {
      try {
        // Try parsing as JSON first
        tiers = JSON.parse(volumeTiers);
      } catch (e) {
        // Parse comma-separated format
        tiers = volumeTiers.split(',').map(tier => {
          const [qty, discount] = tier.split(':').map(v => parseInt(v.trim()));
          return { quantity: qty, discount: discount };
        });
      }
    } else if (Array.isArray(volumeTiers)) {
      tiers = volumeTiers;
    }

    // Sort tiers by quantity (highest first)
    tiers.sort((a, b) => b.quantity - a.quantity);

    // Find applicable tier
    for (const tier of tiers) {
      if (quantity >= tier.quantity) {
        return tier.discount;
      }
    }

    return 0;
  }

  /**
   * Update price display for a product
   */
  updatePrice(productId, variantId, quantity = 1) {
    const priceEl = document.querySelector(
      `.price-b2b[data-product-id="${productId}"][data-variant-id="${variantId}"]`
    );
    
    if (!priceEl) return;

    const pricing = this.calculateDealerPrice(productId, variantId, quantity);
    if (!pricing) return;

    // Update dealer price
    const dealerPriceEl = priceEl.querySelector('.price-b2b__dealer-price');
    if (dealerPriceEl) {
      dealerPriceEl.textContent = this.formatMoney(pricing.dealerPrice);
    }

    // Update savings display
    const savingsEl = priceEl.querySelector('.price-b2b__savings');
    if (savingsEl) {
      let savingsText = `(${pricing.baseDiscount}% off`;
      if (pricing.volumeDiscount > 0) {
        savingsText += ` + ${pricing.volumeDiscount}% volume discount`;
      }
      savingsText += ')';
      savingsEl.textContent = savingsText;
    }

    // Update retail reference
    const retailPriceEl = priceEl.querySelector('.price-b2b__retail-price');
    if (retailPriceEl) {
      retailPriceEl.textContent = this.formatMoney(pricing.retailPrice);
    }

    // Dispatch custom event for other components
    priceEl.dispatchEvent(new CustomEvent('b2b-price-updated', {
      detail: pricing,
      bubbles: true
    }));
  }

  /**
   * Update all prices on the page
   */
  updateAllPrices() {
    Object.keys(this.pricingData).forEach(productId => {
      const data = this.pricingData[productId];
      if (!data.isDealer) return;

      // Find current variant and quantity
      const variantId = this.getCurrentVariant(productId);
      const quantity = this.getCurrentQuantity(productId);

      if (variantId) {
        this.updatePrice(productId, variantId, quantity);
      }
    });
  }

  /**
   * Get current selected variant for product
   */
  getCurrentVariant(productId) {
    // Try radio buttons first (common in product forms)
    const checkedVariant = document.querySelector(
      `input[name="id"][data-product-id="${productId}"]:checked`
    );
    if (checkedVariant) return checkedVariant.value;

    // Try select dropdown
    const variantSelect = document.querySelector(
      `select[name="id"][data-product-id="${productId}"]`
    );
    if (variantSelect) return variantSelect.value;

    // Try data attribute on form
    const form = document.querySelector(`form[data-product-id="${productId}"]`);
    if (form && form.dataset.variantId) return form.dataset.variantId;

    // Default to first variant
    const data = this.pricingData[productId];
    if (data && data.variantPrices) {
      return Object.keys(data.variantPrices)[0];
    }

    return null;
  }

  /**
   * Get current quantity input value
   */
  getCurrentQuantity(productId) {
    const quantityInput = document.querySelector(
      `input[name="quantity"][data-product-id="${productId}"], 
       input[name="quantity"][form*="product-form-${productId}"]`
    );
    
    return quantityInput ? parseInt(quantityInput.value) || 1 : 1;
  }

  /**
   * Setup quantity input listeners
   */
  setupQuantityListeners() {
    const quantityInputs = document.querySelectorAll('input[name="quantity"]');
    
    quantityInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const productId = this.getProductIdFromElement(e.target);
        if (productId) {
          const variantId = this.getCurrentVariant(productId);
          const quantity = parseInt(e.target.value) || 1;
          this.updatePrice(productId, variantId, quantity);
          this.highlightVolumeTier(productId, quantity);
        }
      });

      // Real-time update on input
      input.addEventListener('input', (e) => {
        const productId = this.getProductIdFromElement(e.target);
        if (productId) {
          const variantId = this.getCurrentVariant(productId);
          const quantity = parseInt(e.target.value) || 1;
          this.updatePrice(productId, variantId, quantity);
          this.highlightVolumeTier(productId, quantity);
        }
      });
    });
  }

  /**
   * Setup variant selection listeners
   */
  setupVariantListeners() {
    // Radio buttons
    const variantRadios = document.querySelectorAll('input[name="id"][type="radio"]');
    variantRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        const productId = this.getProductIdFromElement(e.target);
        if (productId) {
          const quantity = this.getCurrentQuantity(productId);
          this.updatePrice(productId, e.target.value, quantity);
        }
      });
    });

    // Select dropdowns
    const variantSelects = document.querySelectorAll('select[name="id"]');
    variantSelects.forEach(select => {
      select.addEventListener('change', (e) => {
        const productId = this.getProductIdFromElement(e.target);
        if (productId) {
          const quantity = this.getCurrentQuantity(productId);
          this.updatePrice(productId, e.target.value, quantity);
        }
      });
    });
  }

  /**
   * Render volume pricing tables
   */
  renderVolumeTables() {
    const tables = document.querySelectorAll('.price-b2b__volume-grid tbody[data-volume-pricing]');
    
    tables.forEach(tbody => {
      const volumeTiers = tbody.dataset.volumePricing;
      if (!volumeTiers) return;

      const table = tbody.closest('.price-b2b__volume-table');
      const priceContainer = table.closest('.price-b2b');
      const productId = priceContainer.dataset.productId;
      const variantId = priceContainer.dataset.variantId;
      
      const data = this.pricingData[productId];
      if (!data) return;

      const variantData = data.variantPrices[variantId];
      if (!variantData) return;

      const baseDealerPrice = variantData.price * (100 - data.dealerDiscount) / 100;

      // Parse tiers
      let tiers = [];
      try {
        tiers = JSON.parse(volumeTiers);
      } catch (e) {
        tiers = volumeTiers.split(',').map(tier => {
          const [qty, discount] = tier.split(':').map(v => parseInt(v.trim()));
          return { quantity: qty, discount: discount };
        });
      }

      // Sort by quantity
      tiers.sort((a, b) => a.quantity - b.quantity);

      // Render rows
      tbody.innerHTML = tiers.map(tier => {
        const priceWithDiscount = baseDealerPrice * (100 - tier.discount) / 100;
        return `
          <tr data-tier-quantity="${tier.quantity}">
            <td>${tier.quantity}+</td>
            <td>${tier.discount}%</td>
            <td class="price-b2b__volume-price">${this.formatMoney(priceWithDiscount)}</td>
          </tr>
        `;
      }).join('');
    });
  }

  /**
   * Highlight active volume tier based on quantity
   */
  highlightVolumeTier(productId, quantity) {
    const tables = document.querySelectorAll(
      `.price-b2b[data-product-id="${productId}"] .price-b2b__volume-grid tbody tr`
    );
    
    let activeFound = false;
    tables.forEach(row => {
      const tierQty = parseInt(row.dataset.tierQuantity);
      row.classList.remove('is-active', 'is-locked');
      
      if (!activeFound && quantity >= tierQty) {
        row.classList.add('is-active');
      } else if (quantity < tierQty) {
        row.classList.add('is-locked');
      }
      
      if (quantity >= tierQty) {
        activeFound = true;
      }
    });
  }

  /**
   * Get product ID from form element
   */
  getProductIdFromElement(element) {
    // Check data attribute
    if (element.dataset.productId) return element.dataset.productId;

    // Check form
    const form = element.closest('form');
    if (form && form.dataset.productId) return form.dataset.productId;

    // Check price container
    const priceContainer = element.closest('.price-b2b');
    if (priceContainer && priceContainer.dataset.productId) {
      return priceContainer.dataset.productId;
    }

    return null;
  }

  /**
   * Format money with Shopify settings
   */
  formatMoney(cents) {
    // This uses Shopify's global money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    
    // Fallback formatter
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Initialize
const b2bPricing = new B2BPricing();

// Export for use in other scripts
window.B2BPricing = B2BPricing;
window.b2bPricingInstance = b2bPricing;

